'use client'

import React, { useRef, useState, useMemo, useEffect } from 'react'
import type { TableBlock as TableBlockProps } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { useDragScroll, useScrollProgress, Scrollbar } from '@/components/ui/horizontal-scroll'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PricingToggle, usePricingToggle } from '@/components/tables'
import type { TableRow, CommitmentTerm } from './types'
import { FLIP_ANIMATION_DURATION } from './types'
import { HeaderCell, BodyCell } from './cells'

type Props = TableBlockProps & {
  disableInnerContainer?: boolean
}

export const TableBlock: React.FC<Props> = ({
  caption,
  style = 'default',
  highlightedColumnIndex,
  firstRowIsHeader,
  tableRows,
  paddingTop,
  paddingBottom,
}) => {
  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [selectedCommitmentTerm, setSelectedCommitmentTerm] = useState<CommitmentTerm>('36')
  const { pricingPeriod } = usePricingToggle()

  // Flip animation state for pricing toggle
  const [isPriceFlipping, setIsPriceFlipping] = useState(false)
  const [displayPeriod, setDisplayPeriod] = useState(pricingPeriod)

  // Flip animation state for commitment dropdown
  const [isCommitmentFlipping, setIsCommitmentFlipping] = useState(false)
  const [displayCommitmentTerm, setDisplayCommitmentTerm] = useState<CommitmentTerm>('36')

  // Use horizontal-scroll hooks for drag-to-scroll and progress tracking
  useDragScroll(scrollContainerRef)
  const { scrollProgress, isVisible: showScrollIndicator } = useScrollProgress(scrollContainerRef)

  const allRows = useMemo(() => (tableRows as TableRow[]) || [], [tableRows])

  // Split rows into header and body based on firstRowIsHeader setting
  const headerRow = useMemo(
    () => (firstRowIsHeader && allRows.length > 0 ? allRows[0] : null),
    [firstRowIsHeader, allRows],
  )
  const bodyRows = useMemo(
    () => (firstRowIsHeader ? allRows.slice(1) : allRows),
    [firstRowIsHeader, allRows],
  )

  const isComparison = style === 'comparison'
  const highlightIndex = highlightedColumnIndex ? highlightedColumnIndex - 1 : -1

  // Check if any row contains a commitment cell to determine if dropdown should be shown
  const hasCommitmentCells = useMemo(() => {
    return allRows.some((row) => row.cells?.some((cell) => cell.variant === 'commitment'))
  }, [allRows])

  // Check if any row contains a price cell (for showing the pricing toggle)
  const hasPriceCells = useMemo(() => {
    return allRows.some((row) => row.cells?.some((cell) => cell.variant === 'price'))
  }, [allRows])

  // Show pricing toggle only if there are price cells and no commitment cells
  const showPricingToggle = hasPriceCells && !hasCommitmentCells

  // Handle the train board flip animation when pricing period changes
  useEffect(() => {
    if (pricingPeriod !== displayPeriod) {
      setIsPriceFlipping(true)
      // Wait for flip-out animation, then update the value
      const timer = setTimeout(() => {
        setDisplayPeriod(pricingPeriod)
        // Continue flipping back in
        setTimeout(() => {
          setIsPriceFlipping(false)
        }, FLIP_ANIMATION_DURATION)
      }, FLIP_ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [pricingPeriod, displayPeriod])

  // Handle the train board flip animation when commitment term changes
  useEffect(() => {
    if (selectedCommitmentTerm !== displayCommitmentTerm) {
      setIsCommitmentFlipping(true)
      // Wait for flip-out animation, then update the value
      const timer = setTimeout(() => {
        setDisplayCommitmentTerm(selectedCommitmentTerm)
        // Continue flipping back in
        setTimeout(() => {
          setIsCommitmentFlipping(false)
        }, FLIP_ANIMATION_DURATION)
      }, FLIP_ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [selectedCommitmentTerm, displayCommitmentTerm])

  if (allRows.length === 0) {
    return null
  }

  // Show controls section if we have either pricing toggle or commitment dropdown
  const showControls = showPricingToggle || hasCommitmentCells

  return (
    <div
      className={cn(
        'box-border flex flex-col items-start justify-start relative w-full',
        paddingClasses,
        showControls && 'gap-8',
      )}
    >
      {/* Controls section - pricing toggle or commitment dropdown */}
      {showControls && (
        <div className="w-full flex justify-end">
          {/* Pricing toggle - only show if table has price cells but no commitment cells */}
          {showPricingToggle && <PricingToggle />}

          {/* Commitment term dropdown - only show if table has commitment cells */}
          {hasCommitmentCells && (
            <>
              <p id="commitment-instructions" className="sr-only">
                Select a commitment term to update prices in the table below
              </p>
              <Select
                value={selectedCommitmentTerm}
                onValueChange={(value: CommitmentTerm) => setSelectedCommitmentTerm(value)}
              >
                <SelectTrigger
                  className="w-auto h-auto gap-2.5 px-3 py-2 border-civo-cloud bg-white rounded-lg body-xsmall text-black/70"
                  aria-describedby="commitment-instructions"
                >
                  <SelectValue>Commitment - {selectedCommitmentTerm} months</SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-white rounded-lg border-civo-cloud">
                  <SelectItem
                    value="12"
                    className="body-xsmall text-black/70 focus:bg-black/[0.02] focus:text-black/70"
                  >
                    12 months
                  </SelectItem>
                  <SelectItem
                    value="24"
                    className="body-xsmall text-black/70 focus:bg-black/[0.02] focus:text-black/70"
                  >
                    24 months
                  </SelectItem>
                  <SelectItem
                    value="36"
                    className="body-xsmall text-black/70 focus:bg-black/[0.02] focus:text-black/70"
                  >
                    36 months
                  </SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>
      )}

      {/* Scrollable table container */}
      <div ref={scrollContainerRef} className="w-full overflow-x-auto scrollbar-hide">
        <table className="w-full border-separate border-spacing-0" role="table">
          {/* Caption for accessibility - visually hidden for screen readers */}
          {caption && <caption className="sr-only">{caption}</caption>}

          {/* Header row - only rendered if firstRowIsHeader is true */}
          {headerRow && (
            <thead>
              <tr>
                {(headerRow.cells || []).map((cell, cellIndex) => {
                  const isHighlighted = isComparison && cellIndex === highlightIndex
                  const isLastColumn = cellIndex === (headerRow.cells?.length || 0) - 1
                  const isLeftOfHighlighted = isComparison && cellIndex === highlightIndex - 1
                  return (
                    <HeaderCell
                      key={cell.id || cellIndex}
                      cell={cell}
                      cellIndex={cellIndex}
                      isHighlighted={isHighlighted}
                      isLastColumn={isLastColumn}
                      isLeftOfHighlighted={isLeftOfHighlighted}
                      isComparison={isComparison}
                      displayPeriod={displayPeriod}
                      displayCommitmentTerm={displayCommitmentTerm}
                    />
                  )
                })}
              </tr>
            </thead>
          )}

          {/* Body rows */}
          <tbody>
            {bodyRows.map((row, rowIndex) => {
              const cells = row.cells || []
              const isLastRow = rowIndex === bodyRows.length - 1

              return (
                <tr
                  key={row.id || rowIndex}
                  className="group transition-colors duration-200 hover:bg-black/[0.02]"
                >
                  {cells.map((cell, cellIndex) => {
                    const isHighlighted = isComparison && cellIndex === highlightIndex
                    const isLastColumn = cellIndex === cells.length - 1
                    const isLeftOfHighlighted = isComparison && cellIndex === highlightIndex - 1

                    return (
                      <BodyCell
                        key={cell.id || cellIndex}
                        cell={cell}
                        cellIndex={cellIndex}
                        isLastColumn={isLastColumn}
                        isLastRow={isLastRow}
                        isLeftOfHighlighted={isLeftOfHighlighted}
                        isHighlighted={isHighlighted}
                        isComparison={isComparison}
                        isFirstRow={rowIndex === 0}
                        hasHeaderRow={!!headerRow}
                        displayPeriod={displayPeriod}
                        displayCommitmentTerm={displayCommitmentTerm}
                        isPriceFlipping={isPriceFlipping}
                        isCommitmentFlipping={isCommitmentFlipping}
                      />
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Scroll indicator - only visible when content overflows */}
      <Scrollbar scrollProgress={scrollProgress} isVisible={showScrollIndicator} className="mt-4" />
    </div>
  )
}
